import { useState, memo, useEffect, useRef } from 'react';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { useNavigate, useLocation } from 'react-router-dom';
// @mui
import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  Slide,
  Popper,
  InputBase,
  InputAdornment,
  ClickAwayListener,
  Autocomplete,
  createFilterOptions,
} from '@mui/material';
// utils
import { bgBlur } from '../../../utils/cssStyles';
import flattenArray from '../../../utils/flattenArray';
// components
import Iconify from '../../../components/iconify';
import { IconButtonAnimate } from '../../../components/animate';
import SearchNotFound from '../../../components/search-not-found';
//
import Navigation from '../nav/config-navigation';
import { useImpersonation } from '../../../contexts/ImpersonationContext';
import { PATH_DASHBOARD } from '../../../routes/paths';

// ----------------------------------------------------------------------

const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 92;

const StyledSearchbar = styled('div')(({ theme }) => ({
  ...bgBlur({ color: theme.palette.background.default }),
  top: 0,
  left: 0,
  zIndex: 99,
  width: '100%',
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  height: APPBAR_MOBILE,
  padding: theme.spacing(0, 3),
  boxShadow: theme.customShadows.z8,
  [theme.breakpoints.up('md')]: {
    height: APPBAR_DESKTOP,
    padding: theme.spacing(0, 5),
  },
}));

const StyledPopper = styled((props) => <Popper {...props} />)(({ theme }) => ({
  left: `8px !important`,
  top: `${APPBAR_MOBILE + 8}px !important`,
  width: 'calc(100% - 16px) !important',
  transform: 'none !important',
  [theme.breakpoints.up('md')]: {
    top: `${APPBAR_DESKTOP + 8}px !important`,
  },
  '& .MuiAutocomplete-paper': {
    padding: theme.spacing(1, 0),
  },
  '& .MuiListSubheader-root': {
    '&.MuiAutocomplete-groupLabel': {
      ...bgBlur({ color: theme.palette.background.neutral }),
      ...theme.typography.overline,
      top: 0,
      margin: 0,
      lineHeight: '48px',
      borderRadius: theme.shape.borderRadius,
    },
  },
  '& .MuiAutocomplete-listbox': {
    '& .MuiAutocomplete-option': {
      padding: theme.spacing(0.5, 2),
      margin: 0,
      display: 'block',
      border: `dashed 1px transparent`,
      borderBottomColor: theme.palette.divider,
      '&:last-of-type': {
        borderBottomColor: 'transparent',
      },
      '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
      },
    },
  },
}));

// ----------------------------------------------------------------------

// As opções da busca saem da barra lateral, e Equipe saiu dela para o menu da foto
// de perfil. O atalho mantém Equipe achada pela busca. Relatórios e Cupons não
// precisam de atalho: estão no submenu de Minhas vendas, que a busca já lê.
const getSearchShortcuts = (isImpersonating) =>
  [
    // A página Equipe é bloqueada sob impersonação, e a barra já a escondia ali.
    ...(isImpersonating ? [] : [{ title: 'Equipe', path: PATH_DASHBOARD.general.team }]),
  ].map((shortcut) => ({ ...shortcut, group: '', indexKey: 'minimal' }));

const getOptionLabel = (option) =>
  `${option?.title || ''} ${option?.path || ''} ${option?.indexKey || ''}`;

// O nome do grupo entra no texto que a busca casa: o item do grupo sem tela própria não é
// opção, e quem digita "Nota Fiscal" ou "Integrações" precisa achar as telas de dentro dele.
const filterOptions = createFilterOptions({
  stringify: (option) => `${option?.group || ''} ${getOptionLabel(option)}`,
});

function Searchbar() {
  const navigate = useNavigate();
  const NavConfig = Navigation();
  const { impersonatedUserId } = useImpersonation();
  const searchShortcuts = getSearchShortcuts(!!impersonatedUserId);

  const { pathname } = useLocation();

  const [open, setOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');

  const searchButtonRef = useRef(null);

  const restoreFocusRef = useRef(false);

  const reduceItems = NavConfig.map((list) => handleLoop(list.items, list.subheader)).flat();

  const allItems = flattenArray(reduceItems)
    .map((option) => {
      const safePath = option?.path || '';
      const safeTitle = option?.title || '';
      const group = splitPath(reduceItems, safePath);

      return {
        group: group && group.length > 1 ? group[0] : option.subheader,
        title: safeTitle,
        path: safePath,
        indexKey: 'minimal',
      };
    })
    // Grupo sem tela própria (caminho vazio ou '#') só abre o submenu na barra lateral; na
    // busca ele não levaria a lugar nenhum. Os filhos dele continuam como opção.
    .filter(({ path }) => path.trim() && path !== '#');

  useEffect(() => {
    if (open) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (open || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    searchButtonRef.current?.focus();
  }, [open]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClick = (path) => {
    if (typeof path !== 'string' || !path.trim() || path === '#') {
      return;
    }

    if (path.includes('http')) {
      window.open(path);
    } else {
      navigate(path);
    }
    handleClose();
  };

  // O Autocomplete consome o primeiro Esc para fechar a lista de sugestões e não deixa
  // ele subir; o Esc que chega aqui é o de quem quer sair da busca.
  const handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    restoreFocusRef.current = true;
    handleClose();
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div>
        {!open && (
          <IconButtonAnimate ref={searchButtonRef} onClick={handleOpen} aria-label="Buscar">
            <Iconify icon="eva:search-fill" />
          </IconButtonAnimate>
        )}

        <Slide direction="down" in={open} mountOnEnter unmountOnExit>
          <StyledSearchbar onKeyDown={handleKeyDown}>
            <Autocomplete
              sx={{ width: 1, height: 1 }}
              autoHighlight
              disablePortal
              disableClearable
              popupIcon={null}
              PopperComponent={StyledPopper}
              // O teclado escolhe pelo onChange. Com autoHighlight, o Enter do MUI troca o texto do
              // campo pelo rótulo da opção antes do keyup, e navegar pelo texto levava ao 404
              // (CKTPL-1618). O valor fica nulo porque as opções nascem a cada render: um valor
              // guardado deixaria de casar com elas, e o onChange só dispara quando o valor muda.
              value={null}
              onChange={(event, option, reason) => {
                if (reason === 'selectOption') handleClick(option?.path);
              }}
              onInputChange={(event, value) => setSearchQuery(value)}
              noOptionsText={<SearchNotFound query={searchQuery} sx={{ py: 10 }} />}
              options={[...allItems, ...searchShortcuts].sort((a, b) =>
                -String(b.group || '').localeCompare(String(a.group || ''))
              )}
              groupBy={(option) => option.group || ''}
              getOptionLabel={getOptionLabel}
              filterOptions={filterOptions}
              renderInput={(params) => (
                <InputBase
                  {...params.InputProps}
                  inputProps={params.inputProps}
                  fullWidth
                  autoFocus
                  placeholder="Buscar na Cakto..."
                  startAdornment={
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  }
                  sx={{ height: 1, typography: 'h6' }}
                />
              )}
              renderOption={(props, option, { inputValue }) => {
                const title = option?.title || '';
                const path = option?.path || '';

                const partsTitle = parse(title, match(title, inputValue));

                const partsPath = path ? parse(path, match(path, inputValue)) : [];

                return (
                  // O clique navega direto, sem a seleção do MUI, que poria o rótulo com o caminho
                  // no campo durante a animação de saída da busca.
                  <Box component="li" {...props} onClick={() => handleClick(path)}>
                    <div>
                      {partsTitle.map((part, index) => (
                        <Box
                          key={index}
                          component="span"
                          sx={{
                            typography: 'subtitle2',
                            textTransform: 'capitalize',
                            color: part.highlight ? 'primary.main' : 'text.primary',
                          }}
                        >
                          {part.text}
                        </Box>
                      ))}
                    </div>

                    {path && (
                      <div>
                        {partsPath.map((part, index) => (
                          <Box
                            key={index}
                            component="span"
                            sx={{
                              typography: 'caption',
                              color: part.highlight ? 'primary.main' : 'text.secondary',
                            }}
                          >
                            {part.text}
                          </Box>
                        ))}
                      </div>
                    )}
                  </Box>
                );
              }}
            />
          </StyledSearchbar>
        </Slide>
      </div>
    </ClickAwayListener>
  );
}

export default memo(Searchbar);

// ----------------------------------------------------------------------

function splitPath(array, key) {
  let stack = array.map((item) => ({
    path: [item.title],
    currItem: item,
  }));

  while (stack.length) {
    const { path, currItem } = stack.pop();

    if (currItem.path === key) {
      return path;
    }

    if (currItem.children?.length) {
      stack = stack.concat(
        currItem.children.map((item) => ({
          path: path.concat(item.title),
          currItem: item,
        }))
      );
    }
  }
  return null;
}

// ----------------------------------------------------------------------

function handleLoop(array, subheader = '') {
  return array?.map((list) => ({
    subheader,
    ...list,
    ...(list.children && {
      children: handleLoop(list.children, subheader),
    }),
  }));
}
